import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import {
    Box,
    Button,
    Card as MuiCard,
    CardContent,
    Container,
    Modal,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Snackbar,
    Alert,
    Chip,
    CircularProgress
} from "@mui/material";
import { spacing } from "@mui/system";
import {
    QrCode as QrCodeIcon,
    ContentCopy as ContentCopyIcon,
    Receipt as ReceiptIcon,
    ArrowBack as ArrowBackIcon
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../utils/axios"; // ou axios padrão do projeto
import useAuth from "../../hooks/useAuth";

const Card = styled(MuiCard)(spacing);

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
};

export default function PublicListagemFaturas({ tax_identifier, showInvoice, setShowInvoice }) {
    const navigate = useNavigate();
    const location = useLocation();

    const { getApi } = useAuth();

    const [faturas, setFaturas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [erroApi, setErroApi] = useState("");
    const [faturaSelecionada, setFaturaSelecionada] = useState(null);
    const [modalTipo, setModalTipo] = useState(null); // 'barras' ou 'pix'
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [openSnackbar, setOpenSnackbar] = useState(false);

    useEffect(() => {
        const buscarFaturas = async () => {
            if (!tax_identifier) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                // Chamada ao endpoint real informado
                const response = await getApi(`/invoice/query/cpfcnpj/${tax_identifier}`);

                // Mapeia o retorno baseado na estrutura fornecida no JSON (ex: response.data.lista)
                const listaRetornada = response.lista ?? [];
                setFaturas(listaRetornada);
            } catch (error) {
                console.error("Erro ao buscar faturas:", error);
                setErroApi("Não foi possível carregar as faturas. Tente novamente mais tarde.");
            } finally {
                setLoading(false);
            }
        };

        buscarFaturas();
    }, [tax_identifier]);

    const handleOpenModal = (fatura, tipo) => {
        setFaturaSelecionada(fatura);
        setModalTipo(tipo);
    };

    const handleCloseModal = () => {
        setFaturaSelecionada(null);
        setModalTipo(null);
    };

    const handleCopyText = async (text) => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
            } else {
                const textArea = document.createElement("textarea");

                textArea.value = text;
                textArea.style.position = "fixed";
                textArea.style.left = "-999999px";
                textArea.style.top = "-999999px";

                document.body.appendChild(textArea);

                textArea.focus();
                textArea.select();

                const success = document.execCommand("copy");

                document.body.removeChild(textArea);

                if (!success) {
                    throw new Error("Falha ao copiar");
                }
            }

            setSnackbarMessage("Código copiado para a área de transferência!");
            setOpenSnackbar(true);

        } catch (err) {
            console.error("Erro ao copiar:", err);

            setSnackbarMessage("Erro ao copiar o código.");
            setOpenSnackbar(true);
        }
    };
    const formatarValor = (valor) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
    };

    const formatarData = (dataStr) => {
        if (!dataStr) return "-";
        const dataObj = new Date(dataStr);
        return dataObj.toLocaleDateString('pt-BR');
    };

    return (
        <React.Fragment>
            <Helmet>
                <title>Listagem de Faturas Pendentes - Cachê Bank</title>
            </Helmet>

            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => setShowInvoice(false)}
                        sx={{ mr: 2 }}
                    >
                        Voltar
                    </Button>
                    <Typography variant="h4" component="h1" fontWeight="bold">
                        Faturas do Cliente
                    </Typography>
                </Box>

                <Typography variant="body1" color="textSecondary" gutterBottom mb={3}>
                    Exibindo registros para o documento: <strong>{tax_identifier}</strong>
                </Typography>

                {erroApi && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {erroApi}
                    </Alert>
                )}

                <Card mb={6} sx={{ boxShadow: 3 }}>
                    <CardContent>
                        {loading ? (
                            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <TableContainer component={Paper} elevation={0}>
                                <Table sx={{ minWidth: 650 }} aria-label="tabela de faturas">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>ID / Fatura</strong></TableCell>
                                            <TableCell><strong>Vencimento</strong></TableCell>
                                            <TableCell><strong>Valor</strong></TableCell>
                                            <TableCell><strong>Status</strong></TableCell>
                                            <TableCell align="center"><strong>Ações de Pagamento</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {faturas.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    <Typography py={3} color="textSecondary">
                                                        Nenhuma fatura encontrada para este documento.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            faturas.map((item, index) => {
                                                const fatura = item.invoice || item;
                                                if (!fatura?.payment_info?.barcode) {
                                                    return null;
                                                }
                                                return (
                                                    <TableRow key={fatura.id || index} hover>
                                                        <TableCell component="th" scope="row">
                                                            <Box display="flex" alignItems="center">
                                                                <ReceiptIcon color="primary" sx={{ mr: 1.5 }} />
                                                                {fatura.id ? fatura.id.substring(0, 8) + '...' : `Fatura #${index + 1}`}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>{formatarData(fatura.payment_info.due_date)}</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>{formatarValor(fatura.payment_info.amount_adjusted || fatura.payment_info.amount)}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={fatura.archived ? "Arquivada" : "Pendente"}
                                                                color={fatura.archived ? "default" : "warning"}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                color="primary"
                                                                startIcon={<ReceiptIcon />}
                                                                onClick={() => handleOpenModal(fatura, 'barras')}
                                                                sx={{ mr: 1, mb: { xs: 1, sm: 0 } }}
                                                            >
                                                                Cód. Barras
                                                            </Button>
                                                            {fatura.payment_info.txid &&

                                                                <Button
                                                                    variant="contained"
                                                                    size="small"
                                                                    color="success"
                                                                    startIcon={<QrCodeIcon />}
                                                                    onClick={() => handleOpenModal(fatura, 'pix')}
                                                                >
                                                                    Pagar Pix
                                                                </Button>
                                                            }

                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardContent>
                </Card>
            </Container>

            {/* Modal de Pagamento */}
            <Modal
                open={Boolean(faturaSelecionada)}
                onClose={handleCloseModal}
                aria-labelledby="modal-titulo-pagamento"

            >
                <Box sx={modalStyle}>
                    {faturaSelecionada && modalTipo === 'barras' && (
                        <>
                            <Typography id="modal-titulo-pagamento" variant="h6" component="h2" gutterBottom fontWeight="bold">
                                Código de Barras
                            </Typography>
                            <Typography variant="body2" color="textSecondary" mb={2}>
                                Copie a linha digitável abaixo para efetuar o pagamento:
                            </Typography>
                            <img src={"https://fatura.cachebank.com.br/api/v3/show/barcode/" + faturaSelecionada.payment_info.barcode}></img>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.100', mb: 2, wordBreak: 'break-all' }}>
                                <Typography variant="body2" fontFamily="monospace" align="center">
                                    {faturaSelecionada.payment_info.digitable_line}
                                </Typography>
                            </Paper>
                            <Box display="flex" justifyContent="flex-end" gap={1}>
                                <Button variant="outlined" onClick={handleCloseModal}>
                                    Fechar
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<ContentCopyIcon />}
                                    onClick={() => handleCopyText(faturaSelecionada.payment_info.digitable_line)}
                                >
                                    Copiar Código
                                </Button>
                            </Box>
                        </>
                    )}

                    {faturaSelecionada && modalTipo === 'pix' && (
                        <>
                            <Typography id="modal-titulo-pagamento" variant="h6" component="h2" gutterBottom fontWeight="bold">
                                Pagamento via Pix
                            </Typography>
                            <Typography variant="body2" color="textSecondary" mb={2}>
                                Escaneie o QR Code abaixo ou utilize o Pix Copia e Cola:
                            </Typography>

                            <Box display="flex" justifyContent="center" my={2}>
                                <Box
                                    sx={{
                                        width: 180,
                                        height: 180,
                                        border: '2px dashed #ccc',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'grey.50',
                                        borderRadius: 2
                                    }}
                                >
                                    <img width="150" src={"https://qrcode.worker.cachebank.com.br/?data=" + faturaSelecionada.payment_info.qrcode_pix}>
                                    </img>
                                </Box>
                            </Box>

                            <Typography variant="body2" align="center" fontWeight="bold" gutterBottom>
                                Valor: {formatarValor(faturaSelecionada.payment_info.amount_adjusted || faturaSelecionada.payment_info.amount)}
                            </Typography>

                            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.100', mb: 2, maxHeight: 80, overflowY: 'auto' }}>
                                <Typography variant="caption" fontFamily="monospace" display="block">
                                    {faturaSelecionada.payment_info.qrcode_pix}
                                </Typography>
                            </Paper>

                            <Box display="flex" justifyContent="flex-end" gap={1}>
                                <Button variant="outlined" onClick={handleCloseModal}>
                                    Fechar
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<ContentCopyIcon />}
                                    onClick={() => handleCopyText(faturaSelecionada.payment_info.qrcode_pix)}
                                >
                                    Copiar Chave Pix
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Modal>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={4000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </React.Fragment>
    );
}